import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentNugget, NUGGET_VARIANTS, PriorityPlaylist } from '@backrs_ui/common-ui';

import { CopyButton } from '@backrs_ui/grow-ui/src/components/clipboard/CopyButton';
import type { Artifact } from '@backrs_ui/grow-ui/src/components/Lubav/streamTypes';
import { DraggableNuggetCard } from '@backrs_ui/grow-ui/src/components/NuggetCards/DraggableNuggetCard';
import { NuggetCardList } from '@backrs_ui/grow-ui/src/components/NuggetCards/NuggetCardList';
import { handlePanelNuggetClick } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { type ResourceKey } from '@backrs_ui/grow-ui/src/models/models';
import { type HighlightedNugget } from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { openSuggestedPlaylistViewStep } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { resourceKeyToTab } from '@backrs_ui/grow-ui/src/utils/streamUtils';

import { Box, Divider, Skeleton, Theme, SxProps, Typography, useMediaQuery } from '@mui/material';

import { CollegeShortlistRenderer } from './CollegeShortlistRenderer';
import { BOX_ORDER, CATEGORY_TITLE_MAP, type InfoBoxConfig } from './InfoBox.config';
import { MarkdownOutput } from './MarkdownOutput';
import { StudyPlanRenderer } from './StudyPlanRenderer';

export type SortedContentItem = Readonly<{
  resourceKey: ResourceKey;
  item: ContentNugget;
  relevanceScore: number;
  variant?: string;
  value_rating?: number;
}>;

export type InfoBoxContentProps = Readonly<{
  currentBox: InfoBoxConfig | undefined;
  sortedContent: readonly SortedContentItem[];
  isFeaturedTab: boolean;
  isSuggestedTab: boolean;
  isLoading?: boolean;
  artifact?: Artifact;
  highlightedNugget: HighlightedNugget | null;
  emptyStateContent?: React.ReactNode;
}>;

const ArtifactRenderer = ({ artifact }: { artifact: Artifact }) => {
  const markdownContentRef = useRef<HTMLDivElement>(null);

  switch (artifact.kind) {
    case 'markdown':
      return (
        <Box sx={artifactContainerSx}>
          <Box sx={copyButtonRowSx}>
            <CopyButton id="markdown-artifact-copy-button" contentRef={markdownContentRef} />
          </Box>
          <Box ref={markdownContentRef}>
            <MarkdownOutput text={artifact.payload} />
          </Box>
        </Box>
      );
    case 'college_shortlist':
      return (
        <Box sx={artifactContainerSx}>
          <CollegeShortlistRenderer payload={artifact.payload} />
        </Box>
      );
    case 'study_plan':
      return (
        <Box sx={artifactContainerSx}>
          <StudyPlanRenderer payload={artifact.payload} />
        </Box>
      );
  }
};

// Suggested tab: one horizontally scrolling section per category, mixed nugget types
const SuggestedSections = ({ sortedContent }: { sortedContent: readonly SortedContentItem[] }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleClick = useCallback(
    (nugget: ContentNugget) => {
      if (nugget.variant === NUGGET_VARIANTS.PLAYLIST && nugget.playlist_info) {
        dispatch(openSuggestedPlaylistViewStep(nugget.playlist_info as PriorityPlaylist));

        return;
      }

      handlePanelNuggetClick(nugget, navigate);
    },
    [navigate, dispatch],
  );

  const sections = BOX_ORDER.map((boxType) => ({
    boxType,
    nuggets: sortedContent
      .filter((content) => resourceKeyToTab[content.resourceKey] === boxType)
      .map((content) => content.item),
  })).filter((section) => section.nuggets.length > 0);

  return (
    <Box sx={suggestedContainerSx}>
      {sections.map(({ boxType, nuggets }) => (
        <Box key={boxType}>
          <Typography variant="text-xl-semibold">{CATEGORY_TITLE_MAP[boxType]}</Typography>
          <Divider sx={sectionDividerSx} />
          <NuggetCardList
            nuggets={nuggets}
            onClick={handleClick}
            direction="scrollingRow"
            hideActions
          />
        </Box>
      ))}
    </Box>
  );
};

export const InfoBoxContent = ({
  currentBox,
  sortedContent,
  isFeaturedTab,
  isSuggestedTab,
  isLoading,
  artifact,
  highlightedNugget,
  emptyStateContent,
}: InfoBoxContentProps) => {
  const isTablet = useMediaQuery('(max-width: 900px)');

  if (!currentBox) {
    return null;
  }

  // FEATURED resources are only populated for nugget-output sessions; an empty list means the
  // output is an artifact, which renders as text.
  if (isLoading) {
    return currentBox.resources.length > 0 ? (
      <CardGridSkeleton isTablet={isTablet} />
    ) : (
      <TextOutputSkeleton />
    );
  }

  if (isFeaturedTab && artifact) {
    return <ArtifactRenderer artifact={artifact} />;
  }

  if (sortedContent.length === 0) {
    if (emptyStateContent) {
      return <>{emptyStateContent}</>;
    }

    return null;
  }

  if (isSuggestedTab) {
    return <SuggestedSections sortedContent={sortedContent} />;
  }

  return (
    <Box sx={gridContainerSx(isTablet)}>
      {sortedContent.map((content, index) => (
        <DraggableNuggetCard
          key={`${content.resourceKey}-${content.item.id}-${index}`}
          nugget={content.item}
          resourceKey={content.resourceKey}
          resourceId={String(content.item.id)}
          highlightedNugget={highlightedNugget}
        />
      ))}
    </Box>
  );
};

// Placeholders shown while a finished session's output is still being fetched, shaped to match
// what will replace them: a card grid for nugget outputs, text lines for artifact outputs.
const CardGridSkeleton = ({ isTablet }: { isTablet: boolean }) => (
  <Box sx={gridContainerSx(isTablet)} aria-hidden>
    {Array.from({ length: 6 }).map((_, index) => (
      <Box key={index} sx={skeletonCardSx}>
        <Box sx={skeletonCardImageSx}>
          <Skeleton variant="rectangular" animation="wave" sx={skeletonImageFillSx} />
        </Box>
        <Skeleton variant="text" animation="wave" width="70%" sx={skeletonCardTitleSx} />
      </Box>
    ))}
  </Box>
);

const TextOutputSkeleton = () => (
  <Box sx={skeletonTextContainerSx} aria-hidden>
    {Array.from({ length: 8 }).map((_, index) => (
      <Skeleton
        key={index}
        variant="text"
        animation="wave"
        width={index % 4 === 3 ? '55%' : '100%'}
      />
    ))}
  </Box>
);

const gridContainerSx =
  (isTablet: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'grid',
    gap: backrs.spacing['spacing-3xl'],
    gridTemplateColumns: 'repeat(auto-fill, minmax(min(270px, 100%), 282px))',
    pt: backrs.spacing['spacing-md'],
    pb: isTablet ? backrs.spacing['spacing-xs'] : backrs.spacing['spacing-lg'],
  });

const skeletonCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const skeletonCardImageSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  aspectRatio: '282 / 180',
  borderRadius: backrs.radius['radius-2xl'],
  overflow: 'hidden',
  position: 'relative',
});

const skeletonImageFillSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};

const skeletonCardTitleSx: SxProps<Theme> = ({ backrs }) => ({
  ml: backrs.spacing['spacing-xl'],
});

const skeletonTextContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  p: backrs.spacing['spacing-md'],
  flexGrow: 1,
});

const suggestedContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  py: backrs.spacing['spacing-md'],
  // ancestor container so NuggetCardList's @container scroll-snap rule can match
  containerType: 'inline-size',
});

const sectionDividerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Gray/700'],
  mt: backrs.spacing['spacing-md'],
  mb: backrs.spacing['spacing-lg'],
});

const artifactContainerSx: SxProps<Theme> = {
  flexGrow: 1,
};

const copyButtonRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: backrs.spacing['spacing-sm'],
});
